import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input, OnChanges,
  Output, SimpleChanges
} from "@angular/core";
import * as _ from "lodash";
import {BzIconDef} from "@bringoz/ui";
import {bzIconSort, bzIconSortAsc, bzIconSortDesc} from "@bringoz/ui/icons";
import {OrderModel} from "../../../models/order.model";

export interface OmsOrdersTableDynamicColumn {
  key?: string;
  id?: string;
  title: string;
  enabled: boolean;
  cls: string;
}

export interface OmsOrdersTableSkeletonRow {
  id: string;
  skeleton: true;
}

export type OmsOrdersTableItem = OrderModel | OmsOrdersTableSkeletonRow;

const SKELETON_ROWS_COUNT = 10;
const DYNAMIC_COLUMN_WIDTH = 150;
const SORT_ICONS: Record<string, BzIconDef> = {asc: bzIconSortAsc, desc: bzIconSortDesc};
const SORT_NAMES = ['sortExternalIdType', 'sortAccountReferenceNameType', 'sortSlaType', 'sortCreatedAtType', 'sortSchedulingSmsType'];

/** Initial width per column class, the bz-table column manager applies them and lets the user resize. */
export const OMS_ORDERS_TABLE_DEFAULT_WIDTHS: Record<string, number> = {
  'col-id': 300,
  'col-account': 220,
  'col-account-external-id': 150,
  'col-branches': 150,
  'col-contact': 170,
  'col-phone': 150,
  'col-location': 300,
  'col-relations': 150,
  'col-program-type': 150,
  'col-sla': 150,
  'col-time-to-deliver': 130,
  'col-fulfillment-status': 160,
  'col-reference-ids': 150,
  'col-created-at': 130,
  'col-pro-number': 130,
  'col-scheduling-sms': 220,
  'col-billing-order': 120,
  'col-items-ids': 150,
  'col-items-types': 150,
  'col-items-barcodes': 150,
  'col-pallets': 100,
  'col-notes': 250
};

/** Column class per settings column key, in table order. Used for the skeleton row cells. */
const COLUMN_CLASSES: Record<string, string> = {
  account: 'col-account',
  accountExternalId: 'col-account-external-id',
  branches: 'col-branches',
  contact: 'col-contact',
  phone: 'col-phone',
  location: 'col-location',
  relations: 'col-relations',
  programType: 'col-program-type',
  sla: 'col-sla',
  timeToDeliver: 'col-time-to-deliver',
  fulfillmentStatus: 'col-fulfillment-status',
  referenceIds: 'col-reference-ids',
  createdAt: 'col-created-at',
  proNumber: 'col-pro-number',
  schedulingSms: 'col-scheduling-sms',
  billingOrder: 'col-billing-order',
  itemsIds: 'col-items-ids',
  itemsTypes: 'col-items-types',
  itemsBarcodes: 'col-items-barcodes',
  pallets: 'col-pallets',
  notes: 'col-notes'
};

const toColumnClass = (prefix: string, value: string): string => `${prefix}-${String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

const SKELETON_ROWS: OmsOrdersTableSkeletonRow[] = Array.from({length: SKELETON_ROWS_COUNT}, (_value, i) => ({id: `__skeleton-${i}`, skeleton: true}));

require('./oms-orders-table.scss');
@Component({
  standalone: false,
  selector: 'app-oms-orders-table',
  templateUrl: './oms-orders-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OmsOrdersTableComponent implements OnChanges {

  // Decorator inputs, not signal inputs: this component is downgraded to AngularJS,
  // and signal inputs do not bind across downgradeComponent.
  @Input() orders: OrderModel[];
  @Input() selectedIds: string[];
  @Input() excludeMode: boolean;
  @Input() columns: any;
  @Input() dynamicColumns: any;
  @Input() packageDynamicColumns: any;
  @Input() isLoading: boolean;
  @Input() isInitialized: boolean;
  @Input() ordersCount: number;
  @Input() sortType: any;
  @Input() canRecoverOrders: boolean;

  @Output() onSelectOrdersChanged = new EventEmitter<any>();
  @Output() fetchNextEvent = new EventEmitter<null>();
  @Output() onSortTypeChange = new EventEmitter<string>();
  @Output() onFilterByType = new EventEmitter<{ type: string, value: string }>();
  @Output() onSendToDispatch = new EventEmitter<string>();
  @Output() onAddNote = new EventEmitter<string>();
  @Output() onSetOrderOnHold = new EventEmitter<string>();
  @Output() onResumeOrder = new EventEmitter<string>();
  @Output() onManuallyCompleteOrder = new EventEmitter<string>();
  @Output() onEdit = new EventEmitter<string>();
  @Output() onDuplicate = new EventEmitter<string>();
  @Output() onPrintBillOfLading = new EventEmitter<string>();
  @Output() onDownloadBillOfLading = new EventEmitter<string>();
  @Output() onSendSchedulingSms = new EventEmitter<string>();
  @Output() onSendSchedulingWhatsapp = new EventEmitter<string>();
  @Output() onViewSchedulingLink = new EventEmitter<string>();
  @Output() onRecover = new EventEmitter<string>();
  @Output() onCancelOrder = new EventEmitter<string>();

  public selectedKeys: Record<string, boolean> = {};
  public isAllSelected = false;
  public isSelectAllIndeterminate = false;
  public enabledDynamicColumns: OmsOrdersTableDynamicColumn[] = [];
  public enabledPackageDynamicColumns: OmsOrdersTableDynamicColumn[] = [];
  public defaultWidths: Record<string, number> = OMS_ORDERS_TABLE_DEFAULT_WIDTHS;
  public tableItems: OmsOrdersTableItem[] = SKELETON_ROWS;
  public sortIcons: Record<string, BzIconDef> = {};
  public skeletonCells: string[] = [];

  ngOnChanges(changes: SimpleChanges) {

    if (changes.selectedIds || changes.excludeMode || changes.ordersCount) {
      this.setSelection();
    }

    if (changes.dynamicColumns || changes.packageDynamicColumns) {
      this.setEnabledColumns();
    }

    if (changes.columns || changes.dynamicColumns || changes.packageDynamicColumns) {
      this.skeletonCells = [
        'col-id',
        ..._.filter(COLUMN_CLASSES, (_cls, key) => !!this.columns?.[key]?.enabled),
        ..._.map([...this.enabledDynamicColumns, ...this.enabledPackageDynamicColumns], 'cls')
      ];
    }

    if (changes.orders || changes.isInitialized) {
      this.tableItems = this.isInitialized ? (this.orders || []) : SKELETON_ROWS;
    }

    if (changes.sortType) {
      this.sortIcons = _.fromPairs(SORT_NAMES.map((name) => [name, this.sortType?.name === name && SORT_ICONS[this.sortType?.type] || bzIconSort]));
    }
  }

  onToggleSelectAll() {
    this.onSelectOrdersChanged.emit({
      selectedIds: [],
      excludeMode: !this.excludeMode
    });
  }

  onToggleOrderSelect(order: OrderModel) {
    const selectedKeys = {...this.selectedKeys, [order.id]: !this.selectedKeys[order.id]};
    this.onSelectOrdersChanged.emit({
      selectedIds: _.keys(_.pickBy(selectedKeys, _.identity)),
      excludeMode: this.excludeMode
    });
  }

  private setSelection() {
    const selectedCount = this.selectedIds?.length || 0;
    this.selectedKeys = _.fromPairs(_.map(this.selectedIds, (selectedId) => [selectedId, true]));
    this.isAllSelected = (!this.excludeMode && !!selectedCount) || (this.excludeMode && !selectedCount);
    this.isSelectAllIndeterminate = (!this.excludeMode && !!selectedCount && this.ordersCount > selectedCount) || (this.excludeMode && !!selectedCount);
  }

  private setEnabledColumns() {
    this.enabledDynamicColumns = _.filter(this.dynamicColumns, 'enabled')
      .map((column) => ({...column, cls: toColumnClass('col-meta', column.key)}));
    this.enabledPackageDynamicColumns = _.filter(this.packageDynamicColumns, 'enabled')
      .map((column) => ({...column, cls: toColumnClass('col-package-meta', column.id)}));
    this.defaultWidths = {
      ...OMS_ORDERS_TABLE_DEFAULT_WIDTHS,
      ..._.fromPairs([...this.enabledDynamicColumns, ...this.enabledPackageDynamicColumns].map((column) => [column.cls, DYNAMIC_COLUMN_WIDTH]))
    };
  }

}
